import { LANGUAGES } from '../i18n/translations';

const siteOrigin = (process.env.REACT_APP_SITE_URL || '').replace(/\/+$/, '');

// Absolute site origin, resolved at runtime when not injected at build time.
export function getSiteOrigin() {
  if (siteOrigin) return siteOrigin;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://owawild.com';
}

const NOINDEX_PREFIXES = ['/account', '/login', '/register', '/admin', '/payment', '/pago', '/mi-cuenta', '/acceso', '/registro'];

// hreflang alternates for the 6 languages + x-default (the clean, auto-detecting
// URL). Each language variant is addressable via ?lang=xx, which the app reads on
// load, so Google can index one URL per language without a routing overhaul.
export function buildLangAlternates(pathname) {
  const origin = getSiteOrigin();
  const base = `${origin}${pathname}`;
  const alternates = LANGUAGES.map((l) => ({
    hreflang: l.code,
    href: `${base}?lang=${l.code}`,
  }));
  alternates.push({ hreflang: 'x-default', href: base });
  return alternates;
}

export function canonicalForLang(pathname, lang) {
  return `${getSiteOrigin()}${pathname}?lang=${lang}`;
}

export function isNoindexPath(pathname) {
  return NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// --- JSON-LD builders ------------------------------------------------------

export function organizationJsonLd() {
  const origin = getSiteOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: 'OWA Wild Adventure',
    url: origin,
    logo: `${origin}/owa-logo-full.webp`,
    image: `${origin}/owa-share-cover.jpg`,
    description:
      'Experiencias marinas y de naturaleza en las Islas Canarias: avistamiento de cetáceos, excursiones en barco, deportes acuáticos y aventura.',
    areaServed: [
      { '@type': 'Place', name: 'Gran Canaria' },
      { '@type': 'Place', name: 'Tenerife' },
      { '@type': 'Place', name: 'Lanzarote' },
      { '@type': 'Place', name: 'Fuerteventura' },
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Muelle Deportivo s/n',
      addressLocality: 'Puerto Rico de Gran Canaria',
      postalCode: '35130',
      addressRegion: 'Islas Canarias',
      addressCountry: 'ES',
    },
    email: 'info@owawild.com',
  };
}

export function websiteJsonLd() {
  const origin = getSiteOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'OWA Wild Adventure',
    url: origin,
    inLanguage: LANGUAGES.map((l) => l.code),
  };
}

// activity.island is stored as a slug (e.g. "gran-canaria"); schema.org
// addressRegion wants a human-readable name, so map the known Canary Islands.
const ISLAND_LABELS = {
  'gran-canaria': 'Gran Canaria',
  tenerife: 'Tenerife',
  lanzarote: 'Lanzarote',
  fuerteventura: 'Fuerteventura',
  'la-palma': 'La Palma',
  'la-gomera': 'La Gomera',
  'el-hierro': 'El Hierro',
};

export function activityJsonLd(activity, pathname) {
  if (!activity) return null;
  const origin = getSiteOrigin();
  const url = `${origin}${pathname}`;
  const images = (activity.gallery && activity.gallery.length ? activity.gallery : [activity.image])
    .filter(Boolean)
    .map((src) => (src.startsWith('http') ? src : `${origin}${src}`));
  const data = {
    '@context': 'https://schema.org',
    // Product unlocks Google's product-snippet/merchant-listing rich results
    // (price, stars); TouristTrip adds correct trip/itinerary semantics on top
    // — same combo GetYourGuide uses in production, verified 2026-10-18.
    '@type': ['Product', 'TouristTrip'],
    name: activity.title,
    description: activity.shortDescription || activity.description,
    image: images,
    url,
    category: activity.category,
    brand: { '@type': 'Brand', name: 'OWA Wild Adventure' },
  };
  if (activity.island || activity.location) {
    data.address = {
      '@type': 'PostalAddress',
      ...(activity.location ? { addressLocality: activity.location } : {}),
      addressRegion: ISLAND_LABELS[activity.island] || activity.island || 'Islas Canarias',
      addressCountry: 'ES',
    };
  }
  if (activity.price) {
    data.offers = {
      '@type': 'Offer',
      price: String(activity.price),
      priceCurrency: activity.paymentCurrency || activity.currency || 'EUR',
      availability: 'https://schema.org/InStock',
      url,
    };
  }
  if (activity.rating && activity.reviews) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(activity.rating),
      reviewCount: String(activity.reviews),
      bestRating: '5',
    };
  }
  return data;
}

// "15 Jul 2025" → "2025-07-15" (ISO 8601) for schema.org; leaves it unchanged if
// it cannot be parsed, so a bad value never breaks the payload.
function toIsoDate(value) {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 10);
}

export function blogPostingJsonLd(post, pathname) {
  if (!post) return null;
  const origin = getSiteOrigin();
  const image = post.image && (post.image.startsWith('http') ? post.image : `${origin}${post.image}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: image ? [image] : undefined,
    datePublished: toIsoDate(post.date),
    dateModified: toIsoDate(post.updated_at) || toIsoDate(post.date),
    author: { '@type': 'Organization', name: post.author || 'OWA Wild Adventure' },
    publisher: {
      '@type': 'Organization',
      name: 'OWA Wild Adventure',
      logo: { '@type': 'ImageObject', url: `${origin}/owa-logo-full.webp` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${origin}${pathname}` },
  };
}

export function breadcrumbJsonLd(items) {
  const origin = getSiteOrigin();
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${origin}${it.path}`,
    })),
  };
}

// Builds FAQPage JSON-LD from real, already-visible page facts (duration,
// included/not-included, meeting point, cancellation policy, requirements).
// Items with no real answer are simply omitted — never fabricate a Q&A.
export function faqPageJsonLd(items) {
  const entries = items.filter((it) => it && it.question && it.answer);
  if (!entries.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((it) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: { '@type': 'Answer', text: it.answer },
    })),
  };
}

export const defaultSeo = {
  title: 'OWA Wild Adventure',
  description: 'Salidas de naturaleza, avistamiento y experiencias marinas en Canarias con una marca ligera, clara y pensada para convertir.',
  image: '/owa-share-cover.jpg',
  robots: 'index,follow',
  type: 'website',
};

function buildAbsoluteUrl(pathname) {
  const base = siteOrigin || window.location.origin;
  return `${base}${pathname}`;
}

export function getRouteSeo(pathname) {
  const routeMeta = [
    {
      match: (path) => path === '/',
      title: 'OWA Wild Adventure | Actividades marinas en Canarias',
      description: 'Reserva experiencias marinas y escapadas de naturaleza en Canarias con una web ligera, clara y orientada a conversion.',
    },
    {
      match: (path) => path === '/activities',
      title: 'Actividades | OWA Wild Adventure',
      description: 'Explora actividades de mar y naturaleza con informacion clara, precios visibles y un checkout preparado para venta directa.',
    },
    {
      match: (path) => path.startsWith('/activity/'),
      title: 'Detalle de actividad | OWA Wild Adventure',
      description: 'Consulta itinerario, imagenes, horarios y reserva online de la actividad seleccionada en OWA.',
    },
    {
      match: (path) => path === '/destinations',
      title: 'Destinos | OWA Wild Adventure',
      description: 'Descubre destinos y zonas de salida de OWA para planificar una escapada marina en Canarias.',
    },
    {
      match: (path) => path === '/blog' || path.startsWith('/blog/'),
      title: 'Blog | OWA Wild Adventure',
      description: 'Guias, noticias y contenido editorial para potenciar el SEO base de OWA con contenido evergreen.',
    },
    {
      match: (path) => path === '/about',
      title: 'Nosotros | OWA Wild Adventure',
      description: 'Conoce la propuesta de valor de OWA, su enfoque local y la experiencia que quiere construir alrededor del mar.',
    },
    {
      match: (path) => path === '/contact',
      title: 'Contacto | OWA Wild Adventure',
      description: 'Contacta con OWA para soporte, dudas comerciales y coordinacion de experiencias personalizadas.',
    },
    {
      match: (path) => path === '/login',
      title: 'Acceso clientes | OWA Wild Adventure',
      description: 'Inicia sesion para ver reservas, datos de cuenta y tu historial de experiencias en OWA.',
      robots: 'noindex,nofollow',
    },
    {
      match: (path) => path === '/register',
      title: 'Crear cuenta | OWA Wild Adventure',
      description: 'Crea tu cuenta de cliente para centralizar reservas, datos y futuras gestiones.',
      robots: 'noindex,nofollow',
    },
    {
      match: (path) => path === '/account',
      title: 'Mi cuenta | OWA Wild Adventure',
      description: 'Panel ligero de cliente con datos personales, reservas y accesos rapidos.',
      robots: 'noindex,nofollow',
    },
    {
      match: (path) => path === '/admin/media',
      title: 'Media library | OWA Wild Adventure',
      description: 'Vista ligera de libreria visual para revisar imagenes, categorias y metadatos.',
      robots: 'noindex,nofollow',
    },
    {
      match: (path) => path === '/pago/resultado',
      title: 'Resultado del pago | OWA Wild Adventure',
      description: 'Consulta el estado de tu reserva y el resultado del pago en OWA.',
      robots: 'noindex,nofollow',
    },
  ];

  const matchedMeta = routeMeta.find((route) => route.match(pathname));

  return {
    ...defaultSeo,
    ...(matchedMeta || {}),
    canonical: buildAbsoluteUrl(pathname),
  };
}
